import { CloudFile } from 'owa-attachment-file-types';
import { createSharingLinkView } from 'owa-compose-link';
import { lazyOperateContent } from 'owa-editor';
import EditorViewState from 'owa-editor/lib/store/schema/EditorViewState';
import { checkSharingLinkUrlDomain, createGetSharingInfoResponse } from 'owa-fileprovider-link';
import {
    CreateSharingLinkFromFileProviderResult,
    GetSharingInfoResponseBase,
} from 'owa-fileprovider-link-services';
import { getGuid } from 'owa-guid';
import { getSharingInformation, onSharingLinkDetected } from 'owa-link-core';
import { OUTLOOK_BEAUTIFIED_PREFIX } from 'owa-attachment-link-common';
import {
    AnchorElementsSource,
    getSharingLinkInfo,
    RecipientContainer,
    SharingLinkInfo,
} from 'owa-link-data';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import AttachmentResultCode from 'owa-service/lib/contract/AttachmentResultCode';
import GetSharingInfoResponse from 'owa-service/lib/contract/GetSharingInfoResponse';
import { trace } from 'owa-trace';
import { Editor } from 'roosterjs-editor-core';
import { LinkIdContainer } from 'owa-attachment-link-common';

export async function replacePlaceholderWithBeautifulLink(
    createLinkResult: CreateSharingLinkFromFileProviderResult,
    file: CloudFile,
    placeholderId: string,
    viewState: EditorViewState,
    getRecipientWells: () => RecipientContainer[],
    fromAddress: string,
    linkIdContainer: LinkIdContainer,
    targetWindow: Window
) {
    checkSharingLinkUrlDomain(createLinkResult.link.webUrl, file.providerType);

    trace.info('insertLinkIntoEditor: createGetSharingInfoResponse');
    let sharingInfo: GetSharingInfoResponseBase;
    if (file.providerType === AttachmentDataProviderType.OneDrivePro) {
        sharingInfo = await getSharingInformation(
            createLinkResult.link.webUrl,
            AnchorElementsSource.InsertLinkFromAttachMenu,
            true /* shouldBeautifyLink*/,
            false /* shouldRehydrate */,
            file.providerType
        );

        // In case GetSharingInfo failure
        if (sharingInfo.ResultCode !== AttachmentResultCode.Success) {
            sharingInfo.FileName = file.name;
            sharingInfo.MimeType = file.type;
            (<GetSharingInfoResponse>sharingInfo).CanonicalUrl = file.location;
        }
    } else {
        sharingInfo = createGetSharingInfoResponse(file, createLinkResult);
    }

    const operateContent = await lazyOperateContent.import();
    const htmlCallback = (editor, range, domUtils) => {
        internalReplacePlaceholderWithBeautifulLink(
            editor,
            viewState,
            placeholderId,
            createLinkResult,
            linkIdContainer,
            sharingInfo,
            file,
            getRecipientWells,
            fromAddress,
            targetWindow
        );
        return {
            selectionRange: null,
            changeSource: null,
        };
    };

    operateContent(viewState, htmlCallback);
}

function internalReplacePlaceholderWithBeautifulLink(
    editor: Editor,
    editorViewState: EditorViewState,
    placeholderId: string,
    createLinkResult: CreateSharingLinkFromFileProviderResult,
    linkIdContainer: LinkIdContainer,
    sharingInfo: GetSharingInfoResponseBase,
    file: CloudFile,
    getRecipientWells: () => RecipientContainer[],
    fromAddress: string,
    targetWindow: Window
) {
    const linkId = OUTLOOK_BEAUTIFIED_PREFIX + getGuid();
    const placeholdersMatchingId = editor.queryElements('#' + placeholderId);
    if (!placeholdersMatchingId || placeholdersMatchingId.length !== 1) {
        trace.warn('Failed to find insert link placeholder');
        return;
    }

    // Raise onSharingLinkDetected to addSharingLink, checkforLinkToken and addComposeLinkViewState
    // VSTO 57638: 1. rename the action
    // 2. we may also trigger checkPerm on this event for ODB links only.
    // 3. Will only call lazyPrefetchWacResourcesForFiles for ODB links.
    // 4. checkforLinkToken only for ODB links
    onSharingLinkDetected(
        linkId,
        createLinkResult.link.webUrl,
        linkIdContainer.sharingLinkIds,
        sharingInfo,
        true /* shouldAddComposeLinkViewState */,
        false /* isPreviouslyBeautified */,
        AnchorElementsSource.InsertLinkFromAttachMenu,
        file.providerType
    );

    const sharingLink: SharingLinkInfo = getSharingLinkInfo(linkId);
    trace.info('insertLinkIntoEditor: createSharingLinkView');
    createSharingLinkView(
        editor,
        editorViewState,
        getRecipientWells,
        fromAddress,
        linkIdContainer.composeId,
        placeholdersMatchingId[0] /* The original anchorElement */,
        sharingLink,
        true /* sharingLinkInfoHasValidFileName */,
        true /* shouldBeautify*/,
        false /* isLinkPreviouslyBeautified */,
        targetWindow
    );
}
